import React from 'react';
import {
	CTAScreen,
	CustomHero,
	ImageTextSection,
	Reviews,
} from '@/components/landing/reusableSections';
import { Columns } from '@/components/landing/reusableSections/Columns';
import {
	useLandingShownEvent,
	useSendToAmplitudeScrolledPercent,
} from '@/hooks';
import { useTranslationWithLocale } from '@/hooks/useTranslationWithLocale';
import { GenerateLandingLinkService, GoogleService } from '@/services';
import {
	convert_1AboveList,
	convert_2AboveList,
	forBusiness_creative_balance,
	forBusiness_creative_changer,
	forBusiness_creative_convert_1,
	forBusiness_creative_convert_2,
	forBusiness_creative_enhance,
	forBusiness_creative_generate,
	forBusiness_creative_perfect,
	reviewsLanding,
} from '@/data';
import {
	AMPLITUDE_EVENTS,
	GOOGLE_EVENTS,
	LANDING_AMPLITUDE_EVENTS,
	LOCAL_STORAGE_KEYS,
} from '@/constants';
import balance from '@/public/images/for-business/creative/balance.webp';
import changer from '@/public/images/for-business/creative/changer.webp';
import convert from '@/public/images/for-business/creative/convert.webp';
import generate from '@/public/images/for-business/creative/generate.webp';
import imgHero from '@/public/images/for-business/creative/hero.webp';
import perfect from '@/public/images/for-business/creative/perfect.webp';
import ctaScreen from '@/public/images/landing/ctaScreen.webp';
import styles from './OnlineCreativeLanding.module.scss';

const OnlineCreativeLanding: React.FC = () => {
	const { t } = useTranslationWithLocale('online-ad-creatives', 'en');
	const { t: tReviews } = useTranslationWithLocale('landing-reviews', 'en');

	const goToProduct = () => {
		GoogleService.logEvent(GOOGLE_EVENTS.LANDING_PASSED);
		window.location.href = GenerateLandingLinkService.webApp();
	};

	useSendToAmplitudeScrolledPercent(
		[50, 75, 100],
		LOCAL_STORAGE_KEYS.ONLINE_CREATIVE_SCROLLED_POINTS,
		AMPLITUDE_EVENTS.WEBAPP_LANDING_SCROLLED,
		{ type: LANDING_AMPLITUDE_EVENTS.ONLINE_CREATIVE_LANDING },
	);

	useLandingShownEvent({
		type: LANDING_AMPLITUDE_EVENTS.ONLINE_CREATIVE_LANDING,
	});

	const converData = [
		forBusiness_creative_convert_1,
		forBusiness_creative_convert_2,
	];
	const convertAboveListData = [convert_1AboveList, convert_2AboveList];
	return (
		<>
			<CustomHero
				image={imgHero}
				title={t('heroData_title')}
				subtitle={t('heroData_subtitle')}
				onClick={goToProduct}
				buttonText={t('getStarted')}
				classes={{
					mainTitle: styles['creative__hero-title'],
					mainSubtitle: styles['creative__hero-subtitle'],
					picture: styles['creative__hero-picture'],
				}}
			/>

			{/* changer */}
			<ImageTextSection
				title={t('changerData_mainTitle')}
				subtitle1={t('changerData_mainSubtitle1')}
				image={changer}
				data={forBusiness_creative_changer}
				extraTextBelow={t('changerData_extraText')}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
			/>

			{/* balance */}
			<ImageTextSection
				title={t('balanceData_mainTitle')}
				subtitle1={t('balanceData_mainSubtitle1')}
				image={balance}
				data={forBusiness_creative_balance}
				extraTextBelow={t('balanceData_extraText')}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
				classes={{
					imageContainer: styles['creative__balance-image-container'],
					list: styles['creative__balance-list'],
				}}
			/>

			<Reviews t={tReviews} data={reviewsLanding} />

			{/* enhances */}
			<Columns
				t={t}
				title={t('enhancesData_mainTitle')}
				data={forBusiness_creative_enhance}
				buttonText={t('getStarted')}
				onClick={goToProduct}
			/>

			{/* generate */}
			<ImageTextSection
				title={t('generateData_mainTitle')}
				image={generate}
				data={forBusiness_creative_generate}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
				classes={{
					section: styles['creative__generate-section'],
				}}
			/>

			{/* convert */}
			<ImageTextSection
				title={t('convertData_mainTitle')}
				image={convert}
				data={converData}
				specialListItem
				specialTextsAboveList={convertAboveListData}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
				classes={{
					section: styles['creative__convert-section'],
					itemInRowList: styles['creative__convert-item-in-row'],
					aboveItem: styles['creative__convert-above'],
					imageContainer: styles['creative__convert-image-container'],
					list: styles['creative__convert-list'],
				}}
			/>

			{/* perfect */}
			<ImageTextSection
				title={t('perfectData_mainTitle')}
				image={perfect}
				data={forBusiness_creative_perfect}
				buttonText={t('getStarted')}
				t={t}
				onClick={goToProduct}
			/>

			{/* TryLift */}
			<CTAScreen
				title1={t('tryLiftData_title1')}
				title_green={t('tryLiftData_greenText')}
				title2={t('tryLiftData_title2')}
				subtitle={t('tryLiftData_subtitle')}
				image={ctaScreen}
				altText={t('tryLiftData_altText')}
				buttonText={t('getStarted')}
				onClick={goToProduct}
				classes={{
					title: styles['creative__tryLift-title'],
					subtitleClass: styles['creative__tryLift-subtitle'],
				}}
			/>
		</>
	);
};

export default OnlineCreativeLanding;
